"use client";

import type { ScaleBand, ScaleTime } from "d3-scale";
import { motion, useTransform } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "./axis-label-budget";
import {
  axisLabelFlowOffsets,
  CHART_X_AXIS_ROW_CLASS,
  useChartAxisSlots,
} from "./chart-axis-frame";
import { useChart, useChartStable } from "./chart-context";
import { sharedDateAxisPart, shortDateFmt } from "./chart-formatters";
import { DEFAULT_Y_DOMAIN_TWEEN_MS } from "./chart-phase";
import { LINE_LOADING_PULSE_EASE } from "./line-loading-timing";
import {
  ProjectedAxisLabels,
  useChartProjection,
  useProjectionProgress,
} from "./projection";
import { autoContrastColor } from "./resolve-css-color";
import { isBandScale, type XRawValue } from "./x-scale";

const X_AXIS_POSITION_TWEEN_MS = DEFAULT_Y_DOMAIN_TWEEN_MS;

export interface XAxisProps {
  /** Number of ticks to show (including first and last). Default: 5. */
  numTicks?: number;
  /** Width of the date ticker box for fade calculation. Default: 50 */
  tickerHalfWidth?: number;
  /**
   * `"data"` — tick labels snap to data rows so crosshair and tooltip stay aligned (default).
   * `"domain"` — evenly spaced ticks across the time domain (may not align with hover).
   */
  tickMode?: "domain" | "data";
  /**
   * Container background color (concrete color, not CSS var). When set and parseable,
   * axis-label text color is auto-contrasted (white on dark, neutral-900 on light).
   */
  background?: string;
}

interface AxisTick {
  date: Date;
  x: number;
  label: string;
}

const LABEL_GUTTER = 6;
/** No neighbour to collide with, so a lone label may use a generous budget. */
const LONE_LABEL_WIDTH = 120;

interface XAxisLabelProps {
  displayLabel: string;
  fullLabel: string;
  x: number;
  boxWidth: number;
  crosshairX: number | null;
  hoveredLabel: string | null;
  isHovering: boolean;
  tickerHalfWidth: number;
  animatePosition: boolean;
  textColor?: string;
  /** Flow layout: the box is a flex item offset by margin. Overlay: absolute. */
  offsetLeft: number | null;
}

function XAxisLabel({
  displayLabel,
  fullLabel,
  x,
  boxWidth,
  crosshairX,
  hoveredLabel,
  isHovering,
  tickerHalfWidth,
  animatePosition,
  textColor,
  offsetLeft,
}: XAxisLabelProps) {
  const fadeBuffer = 20;
  const fadeRadius = tickerHalfWidth + fadeBuffer;

  let opacity = 1;
  if (isHovering && crosshairX !== null) {
    const distance = Math.abs(x - crosshairX);
    if (distance < tickerHalfWidth) {
      opacity = 0;
    } else if (hoveredLabel && fullLabel === hoveredLabel) {
      opacity = 0;
    } else if (distance < fadeRadius) {
      opacity = (distance - tickerHalfWidth) / fadeBuffer;
    }
  }

  const flow = offsetLeft !== null;
  const positionTransition = animatePosition
    ? `${flow ? "margin-left" : "left"} ${X_AXIS_POSITION_TWEEN_MS}ms cubic-bezier(${LINE_LOADING_PULSE_EASE.join(", ")})`
    : undefined;

  return (
    <div
      className={
        flow ? "flex shrink-0 justify-center" : "absolute flex justify-center"
      }
      style={
        flow
          ? {
              marginLeft: offsetLeft,
              width: boxWidth,
              transition: positionTransition,
            }
          : {
              left: x - boxWidth / 2,
              bottom: 12,
              width: boxWidth,
              transition: positionTransition,
            }
      }
    >
      <span
        className="text-center text-chart-label text-xs leading-tight tabular-nums [overflow-wrap:anywhere]"
        style={{
          opacity,
          color: textColor,
          maxWidth: axisLabelMaxWidth("xAxisTick", boxWidth - LABEL_GUTTER),
          transition: "opacity 0.4s ease-in-out",
        }}
      >
        {displayLabel}
      </span>
    </div>
  );
}

const MAX_GAP_LAYOUTS = 400;

function binomial(n: number, k: number): number {
  if (k < 0 || k > n) {
    return 0;
  }
  let result = 1;
  for (let i = 0; i < k; i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return result;
}

/** All ways to split `span` into `parts` positive integer gaps. */
function composePositiveSum(sum: number, parts: number): number[][] {
  if (parts === 1) {
    return sum >= 1 ? [[sum]] : [];
  }

  const layouts: number[][] = [];
  for (let gap = 1; gap <= sum - (parts - 1); gap++) {
    for (const tail of composePositiveSum(sum - gap, parts - 1)) {
      layouts.push([gap, ...tail]);
    }
  }
  return layouts;
}

function gapsToIndices(gaps: number[]): number[] {
  const indices = [0];
  let position = 0;
  for (const gap of gaps) {
    position += gap;
    indices.push(position);
  }
  return indices;
}

function indicesForTickCount(length: number, tickCount: number): number[] {
  const span = length - 1;
  if (span <= 0) {
    return [0];
  }

  const rawIndices = Array.from({ length: tickCount }, (_, index) =>
    Math.round((index / (tickCount - 1)) * span)
  );

  const indices = [...new Set(rawIndices)].sort((a, b) => a - b);
  if (indices[0] !== 0) {
    indices.unshift(0);
  }
  if (indices.at(-1) !== span) {
    indices.push(span);
  }

  return [...new Set(indices)].sort((a, b) => a - b);
}

function allIndexLayouts(length: number, tickCount: number): number[][] {
  const span = length - 1;
  if (span <= 0) {
    return [[0]];
  }

  const gapCount = tickCount - 1;
  if (gapCount <= 0) {
    return [[0]];
  }

  const layoutCount = binomial(span - 1, gapCount - 1);
  if (layoutCount > MAX_GAP_LAYOUTS) {
    return [indicesForTickCount(length, tickCount)];
  }

  return composePositiveSum(span, gapCount).map(gapsToIndices);
}

function dedupeIndicesByLabel(
  indices: number[],
  data: Record<string, unknown>[],
  dateLabels: string[],
  xAccessor: (d: Record<string, unknown>) => Date | number | string,
  locale?: string,
): number[] {
  const seenLabels = new Set<string>();
  const deduped: number[] = [];

  for (const index of indices) {
    const point = data[index];
    if (!point) {
      continue;
    }
    const raw = xAccessor(point);
    const label =
      dateLabels[index] ??
      (raw instanceof Date ? shortDateFmt(locale).format(raw) : String(raw));
    if (seenLabels.has(label)) {
      continue;
    }
    seenLabels.add(label);
    deduped.push(index);
  }

  return deduped;
}

interface TickLayoutScore {
  score: number;
  symmetryPenalty: number;
  countDistance: number;
  /** 0 = smallest gap at end, 1 = at start, 2 = in the middle */
  edgePreference: number;
}

function indexGaps(indices: number[]): number[] {
  const gaps: number[] = [];
  for (let i = 1; i < indices.length; i++) {
    const current = indices[i];
    const previous = indices[i - 1];
    if (current == null || previous == null) {
      continue;
    }
    gaps.push(current - previous);
  }
  return gaps;
}

function smallestGapEdgePreference(indices: number[]): number {
  const gaps = indexGaps(indices);
  const smallestGap = Math.min(...gaps);
  const smallestGapIndex = gaps.indexOf(smallestGap);
  if (smallestGapIndex === gaps.length - 1) {
    return 0;
  }
  if (smallestGapIndex === 0) {
    return 1;
  }
  return 2;
}

function scoreTickLayout(
  indices: number[],
  resolveXPx: (index: number) => number,
  targetCount: number
): TickLayoutScore {
  if (indices.length < 2) {
    return {
      score: Number.POSITIVE_INFINITY,
      symmetryPenalty: Number.POSITIVE_INFINITY,
      countDistance: Number.POSITIVE_INFINITY,
      edgePreference: Number.POSITIVE_INFINITY,
    };
  }

  const pixelGaps: number[] = [];
  for (let i = 1; i < indices.length; i++) {
    const current = indices[i];
    const previous = indices[i - 1];
    if (current == null || previous == null) {
      continue;
    }
    pixelGaps.push(resolveXPx(current) - resolveXPx(previous));
  }

  const minGap = Math.min(...pixelGaps);
  const maxGap = Math.max(...pixelGaps);
  const meanGap =
    pixelGaps.reduce((sum, gap) => sum + gap, 0) / pixelGaps.length;
  const spreadRatio =
    meanGap > 0 ? (maxGap - minGap) / meanGap : maxGap - minGap;
  const countDistance = Math.abs(indices.length - targetCount);

  const gaps = indexGaps(indices);
  const smallestGap = Math.min(...gaps);
  const smallestGapIndex = gaps.indexOf(smallestGap);
  const interiorPenalty =
    smallestGapIndex > 0 && smallestGapIndex < gaps.length - 1 ? 0.08 : 0;

  const symmetryPenalty =
    gaps.reduce((penalty, gap, index) => {
      return penalty + Math.abs(gap - (gaps.at(-1 - index) ?? gap));
    }, 0) / gaps.length;

  return {
    score:
      spreadRatio +
      0.1 * countDistance +
      interiorPenalty +
      symmetryPenalty * 0.02,
    symmetryPenalty,
    countDistance,
    edgePreference: smallestGapEdgePreference(indices),
  };
}

function isBetterTickLayout(
  next: TickLayoutScore,
  best: TickLayoutScore,
  nextCountDistance: number,
  bestCountDistance: number
): boolean {
  if (next.score < best.score - 1e-6) {
    return true;
  }
  if (Math.abs(next.score - best.score) > 1e-6) {
    return false;
  }
  if (nextCountDistance < bestCountDistance) {
    return true;
  }
  if (nextCountDistance > bestCountDistance) {
    return false;
  }
  if (next.symmetryPenalty < best.symmetryPenalty - 1e-6) {
    return true;
  }
  if (next.symmetryPenalty > best.symmetryPenalty + 1e-6) {
    return false;
  }
  return next.edgePreference < best.edgePreference;
}

/**
 * Picks tick indices with the most even on-screen spacing. Tries
 * `targetCount ± 1` and evaluates every gap layout when feasible.
 */
export function selectEvenlySpacedIndices(
  length: number,
  targetCount: number,
  options?: {
    data?: Record<string, unknown>[];
    dateLabels?: string[];
    xAccessor?: (d: Record<string, unknown>) => Date;
    resolveXPx?: (index: number) => number;
    locale?: string;
  }
): number[] {
  if (length <= 0) {
    return [];
  }
  if (length === 1) {
    return [0];
  }
  if (length <= targetCount) {
    return Array.from({ length }, (_, index) => index);
  }

  const resolveXPx = options?.resolveXPx ?? ((index: number) => index);

  const minCount = Math.max(2, targetCount - 1);
  const maxCount = Math.min(length, targetCount + 1);

  let bestIndices = indicesForTickCount(length, targetCount);
  let bestScore = scoreTickLayout(bestIndices, resolveXPx, targetCount);
  let bestCountDistance = bestScore.countDistance;

  for (let tickCount = minCount; tickCount <= maxCount; tickCount++) {
    for (const rawIndices of allIndexLayouts(length, tickCount)) {
      const indices =
        options?.data && options.dateLabels && options.xAccessor
          ? dedupeIndicesByLabel(
              rawIndices,
              options.data,
              options.dateLabels,
              options.xAccessor,
              options.locale
            )
          : rawIndices;

      if (indices.length < 2) {
        continue;
      }

      const layoutScore = scoreTickLayout(indices, resolveXPx, targetCount);
      const countDistance = Math.abs(indices.length - targetCount);

      if (
        isBetterTickLayout(
          layoutScore,
          bestScore,
          countDistance,
          bestCountDistance
        )
      ) {
        bestIndices = indices;
        bestScore = layoutScore;
        bestCountDistance = countDistance;
      }
    }
  }

  return bestIndices;
}

function buildDataAlignedTicks({
  data,
  dateLabels,
  marginLeft,
  targetTickCount,
  xAccessor,
  xScale,
  locale,
}: {
  data: Record<string, unknown>[];
  dateLabels: string[];
  marginLeft: number;
  targetTickCount: number;
  xAccessor: (d: Record<string, unknown>) => Date;
  xScale: (date: Date) => number | undefined;
  locale?: string;
}): AxisTick[] {
  const seenLabels = new Set<string>();
  const ticks: AxisTick[] = [];

  const resolveXPx = (index: number) => {
    const point = data[index];
    if (!point) {
      return index;
    }
    return xScale(xAccessor(point)) ?? 0;
  };

  for (const index of selectEvenlySpacedIndices(data.length, targetTickCount, {
    data,
    dateLabels,
    resolveXPx,
    xAccessor,
    locale,
  })) {
    const point = data[index];
    if (!point) {
      continue;
    }
    const date = xAccessor(point);
    const label = dateLabels[index] ?? shortDateFmt(locale).format(date);
    if (seenLabels.has(label)) {
      continue;
    }
    seenLabels.add(label);
    ticks.push({
      date,
      label,
      x: (xScale(date) ?? 0) + marginLeft,
    });
  }

  return ticks;
}

function buildDomainTicks({
  marginLeft,
  numTicks,
  xScale,
  locale,
}: {
  marginLeft: number;
  numTicks: number;
  xScale: {
    domain: () => Date[];
    (date: Date): number | undefined;
  };
  locale?: string;
}): AxisTick[] {
  const domain = xScale.domain();
  const startDate = domain[0];
  const endDate = domain[1];

  if (!(startDate && endDate)) {
    return [];
  }

  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  const timeRange = endTime - startTime;
  const tickCount = Math.max(2, numTicks);
  const seenLabels = new Set<string>();
  const ticks: AxisTick[] = [];

  for (let i = 0; i < tickCount; i++) {
    const t = i / (tickCount - 1);
    const date = new Date(startTime + t * timeRange);
    const label = shortDateFmt(locale).format(date);
    if (seenLabels.has(label)) {
      continue;
    }
    seenLabels.add(label);
    ticks.push({
      date,
      label,
      x: (xScale(date) ?? 0) + marginLeft,
    });
  }

  return ticks;
}

function domainExtendsPastData(
  data: Record<string, unknown>[],
  xAccessor: (d: Record<string, unknown>) => Date,
  xScale: { domain: () => Date[] }
): boolean {
  if (data.length === 0) {
    return false;
  }
  const domainEnd = xScale.domain()[1];
  const lastPoint = data.at(-1);
  if (!(domainEnd && lastPoint)) {
    return false;
  }
  return Number(domainEnd) > Number(xAccessor(lastPoint));
}

/** Domain ticks for the projection tail when brush keeps data-aligned labels. */
function appendProjectionTailTicks(
  ticks: AxisTick[],
  data: Record<string, unknown>[],
  xAccessor: (d: Record<string, unknown>) => Date,
  xScale: {
    domain: () => Date[];
    (date: Date): number | undefined;
  },
  marginLeft: number,
  maxExtraTicks: number,
  locale?: string
): AxisTick[] {
  if (data.length === 0 || maxExtraTicks <= 0) {
    return ticks;
  }

  const lastPoint = data.at(-1);
  const domainEnd = xScale.domain()[1];
  if (!(lastPoint && domainEnd)) {
    return ticks;
  }

  const lastDate = xAccessor(lastPoint);
  const startTime = lastDate.getTime();
  const endTime = domainEnd.getTime();
  if (endTime <= startTime) {
    return ticks;
  }

  const seenLabels = new Set(ticks.map((tick) => tick.label));
  const extras: AxisTick[] = [];
  const extraCount = Math.min(maxExtraTicks, 3);

  for (let i = 1; i <= extraCount; i++) {
    const date = new Date(
      startTime + (i / (extraCount + 1)) * (endTime - startTime)
    );
    const label = shortDateFmt(locale).format(date);
    if (seenLabels.has(label)) {
      continue;
    }
    seenLabels.add(label);
    extras.push({
      date,
      label,
      x: (xScale(date) ?? 0) + marginLeft,
    });
  }

  const endLabel = shortDateFmt(locale).format(domainEnd);
  if (!seenLabels.has(endLabel)) {
    extras.push({
      date: domainEnd,
      label: endLabel,
      x: (xScale(domainEnd) ?? 0) + marginLeft,
    });
  }

  if (extras.length === 0) {
    return ticks;
  }

  return [...ticks, ...extras].sort((a, b) => a.x - b.x);
}

/**
 * Longest whole-word prefix shared by every label. Returns "" when there is none.
 * E.g. ["Woche 1", "Woche 2", ..., "Woche 12"] → "Woche".
 */
function commonWordPrefix(labels: string[]): string {
  if (labels.length < 2) return "";
  const first = (labels[0] ?? "").trim();
  const words = first.split(/\s+/);
  if (words.length < 2) return "";
  for (let n = words.length - 1; n >= 1; n--) {
    const prefix = `${words.slice(0, n).join(" ")} `;
    if (labels.every((l) => l.startsWith(prefix))) {
      return prefix.trimEnd();
    }
  }
  return "";
}

function buildBandTicks({
  bandScale,
  dateLabels,
  marginLeft,
}: {
  bandScale: ScaleBand<string>;
  dateLabels: string[];
  marginLeft: number;
}): { label: string; x: number; key: string }[] {
  const domain = bandScale.domain();
  const half = bandScale.bandwidth() / 2;
  return domain.map((key, index) => {
    const label = dateLabels[index] ?? key;
    return {
      label,
      x: (bandScale(key) ?? 0) + half + marginLeft,
      key: `${label}-${index}`,
    };
  });
}

function buildLinearTicks({
  linearScale,
  data,
  dateLabels,
  xAccessor,
  marginLeft,
}: {
  linearScale: (value: number) => number | undefined;
  data: Record<string, unknown>[];
  dateLabels: string[];
  xAccessor: (d: Record<string, unknown>) => XRawValue;
  marginLeft: number;
}): { label: string; x: number; key: string }[] {
  const seen = new Set<number>();
  const ticks: { label: string; x: number; key: string }[] = [];
  data.forEach((point, index) => {
    const raw = xAccessor(point);
    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || seen.has(numeric)) return;
    seen.add(numeric);
    const label = dateLabels[index] ?? String(numeric);
    ticks.push({
      label,
      x: (linearScale(numeric) ?? 0) + marginLeft,
      key: `${numeric}-${index}`,
    });
  });
  return ticks;
}

/** Overlay mode has no rail to park a derived caption in. */
const ignoreDerivedLabel = () => undefined;

export function XAxis(props: XAxisProps) {
  const { containerRef } = useChartStable();
  const slots = useChartAxisSlots();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const target = slots?.xRail ?? containerRef.current;
  if (!(mounted && target)) {
    return null;
  }

  return (
    <XAxisInner
      {...props}
      authoredLabel={slots?.xAxisLabel ?? null}
      framed={slots?.xRail != null}
      onDerivedLabel={slots?.registerDerivedXAxisLabel ?? ignoreDerivedLabel}
      target={target}
    />
  );
}

const XAxisInner = memo(function XAxisInner({
  numTicks = 5,
  tickerHalfWidth = 50,
  tickMode = "data",
  background,
  authoredLabel,
  framed,
  onDerivedLabel,
  target,
}: XAxisProps & {
  authoredLabel: string | null;
  framed: boolean;
  onDerivedLabel: (label: string | null) => void;
  target: HTMLElement;
}) {
  const textColor = useMemo(() => autoContrastColor(background), [background]);
  const { intlLocale } = useViewerLanguage();
  const projection = useChartProjection();
  const projectionProgress = useProjectionProgress(projection);
  const htmlOpacity = useTransform(projectionProgress, [0, 0.4], [1, 0]);
  const {
    xScale: ctxXScale,
    xScaleKind,
    margin,
    tooltipData,
    data,
    xAccessor: ctxXAccessor,
    dateLabels,
    xDomain,
  } = useChart();

  const isBand = xScaleKind === "band" || isBandScale(ctxXScale);
  const isLinear = !isBand && xScaleKind === "linear";
  const timeScale =
    isBand || isLinear ? null : (ctxXScale as ScaleTime<number, number>);
  const bandScale = isBand ? (ctxXScale as ScaleBand<string>) : null;
  const linearScale = isLinear
    ? (ctxXScale as unknown as (value: number) => number | undefined)
    : null;
  const timeXAccessor = ctxXAccessor as (d: Record<string, unknown>) => Date;
  const rawXAccessor = ctxXAccessor as (d: Record<string, unknown>) => XRawValue;

  const effectiveTickCount = data.length > 0 ? data.length : numTicks;

  interface TickRender {
    label: string;
    x: number;
    key: string;
    /** Only set on a time axis — the shared-month rule needs the calendar. */
    date?: Date;
  }

  const labelsToShow = useMemo<TickRender[]>(() => {
    if (isBand && bandScale) {
      return buildBandTicks({
        bandScale,
        dateLabels,
        marginLeft: margin.left,
      });
    }

    if (isLinear && linearScale) {
      return buildLinearTicks({
        linearScale,
        data,
        dateLabels,
        xAccessor: rawXAccessor,
        marginLeft: margin.left,
      });
    }

    if (!timeScale) return [];

    const projectionExtendsScale =
      tickMode === "data" &&
      domainExtendsPastData(data, timeXAccessor, timeScale);

    const toRender = (ticks: AxisTick[]): TickRender[] =>
      ticks.map((t) => ({
        label: t.label,
        x: t.x,
        key: `${t.date.getTime()}-${t.x}`,
        date: t.date,
      }));

    if (tickMode === "domain") {
      return toRender(
        buildDomainTicks({
          marginLeft: margin.left,
          numTicks,
          xScale: timeScale,
          locale: intlLocale,
        }),
      );
    }

    if (projectionExtendsScale && xDomain == null) {
      return toRender(
        buildDomainTicks({
          marginLeft: margin.left,
          numTicks,
          xScale: timeScale,
          locale: intlLocale,
        }),
      );
    }

    const dataTicks = buildDataAlignedTicks({
      data,
      dateLabels,
      marginLeft: margin.left,
      targetTickCount: effectiveTickCount,
      xAccessor: timeXAccessor,
      xScale: timeScale,
      locale: intlLocale,
    });

    if (projectionExtendsScale && xDomain != null) {
      return toRender(
        appendProjectionTailTicks(
          dataTicks,
          data,
          timeXAccessor,
          timeScale,
          margin.left,
          Math.max(1, numTicks - dataTicks.length + 1),
          intlLocale,
        ),
      );
    }

    return toRender(dataTicks);
  }, [
    isBand,
    bandScale,
    isLinear,
    linearScale,
    rawXAccessor,
    timeScale,
    tickMode,
    xDomain,
    data,
    dateLabels,
    timeXAccessor,
    margin.left,
    numTicks,
    effectiveTickCount,
    intlLocale,
  ]);

  /**
   * What every rendered tick shares — a calendar month on a time axis, a whole
   * leading word otherwise. Lifted out of the ticks and parked in the rail's
   * caption, so no tick has to carry it. Only possible inside a frame, and
   * never against an author-set caption, which would swallow the month.
   *
   * Stable across resizes: every tick layout keeps the first and last data
   * point, so the calendar range the ticks span does not change with their
   * count. Brushing changes it, which is the point.
   */
  const hoisted = useMemo<{ caption: string; labels: string[] } | null>(() => {
    if (!framed || authoredLabel) {
      return null;
    }

    const raw = labelsToShow.map((t) => t.label);
    const dates = labelsToShow.flatMap((t) => (t.date ? [t.date] : []));
    if (dates.length === labelsToShow.length) {
      return sharedDateAxisPart(dates, raw, intlLocale);
    }

    const prefix = commonWordPrefix(raw);
    if (!prefix) {
      return null;
    }
    return {
      caption: prefix,
      labels: raw.map((label) => label.slice(prefix.length).trimStart()),
    };
  }, [labelsToShow, framed, authoredLabel, intlLocale]);

  const hoistedCaption = hoisted?.caption ?? null;

  useEffect(() => {
    onDerivedLabel(hoistedCaption);
  }, [hoistedCaption, onDerivedLabel]);

  useEffect(() => () => onDerivedLabel(null), [onDerivedLabel]);

  /**
   * Without a caption slot the common word prefix stays on the first tick:
   * ["Woche 1", "Woche 2", ...] → ["Woche 1", "2", ...]. DatePill and tooltip
   * always get the full label via context's dateLabels.
   */
  const displayLabels = useMemo<string[]>(() => {
    const raw = labelsToShow.map((t) => t.label);
    if (hoisted) return hoisted.labels;
    const prefix = commonWordPrefix(raw);
    if (!prefix) return raw;
    return raw.map((label, i) =>
      i === 0 ? label : label.slice(prefix.length).trimStart(),
    );
  }, [labelsToShow, hoisted]);

  const labelBoxWidth = useMemo(() => {
    if (labelsToShow.length < 2) {
      return LONE_LABEL_WIDTH;
    }
    let narrowest = Number.POSITIVE_INFINITY;
    for (let i = 1; i < labelsToShow.length; i++) {
      const gap = Math.abs(
        (labelsToShow[i]?.x ?? 0) - (labelsToShow[i - 1]?.x ?? 0),
      );
      if (gap > 0 && gap < narrowest) {
        narrowest = gap;
      }
    }
    return Number.isFinite(narrowest) ? narrowest : LONE_LABEL_WIDTH;
  }, [labelsToShow]);

  const isHovering = tooltipData !== null;
  const crosshairX = tooltipData ? tooltipData.x + margin.left : null;
  const hoveredLabel = useMemo(() => {
    if (!(isHovering && tooltipData)) return null;
    const fromContext = dateLabels[tooltipData.index];
    if (fromContext) return fromContext;
    if (isBand) {
      const raw = rawXAccessor(tooltipData.point);
      return raw instanceof Date ? shortDateFmt(intlLocale).format(raw) : String(raw);
    }
    return shortDateFmt(intlLocale).format(timeXAccessor(tooltipData.point));
  }, [isHovering, tooltipData, dateLabels, isBand, rawXAccessor, timeXAccessor, intlLocale]);

  const flowOffsets = framed
    ? axisLabelFlowOffsets(
        labelsToShow.map((item) => item.x),
        labelBoxWidth
      )
    : null;

  const labels = labelsToShow.map((item, index) => (
    <XAxisLabel
      animatePosition={xDomain == null}
      boxWidth={labelBoxWidth}
      crosshairX={crosshairX}
      displayLabel={displayLabels[index] ?? item.label}
      fullLabel={item.label}
      hoveredLabel={hoveredLabel}
      isHovering={isHovering}
      key={item.key}
      offsetLeft={flowOffsets?.[index] ?? null}
      textColor={textColor}
      tickerHalfWidth={tickerHalfWidth}
      x={item.x}
    />
  ));

  if (projection) {
    return (
      <>
        {createPortal(
          <motion.div
            className={
              framed
                ? cn("pointer-events-none", CHART_X_AXIS_ROW_CLASS)
                : "pointer-events-none absolute inset-0"
            }
            style={{ opacity: htmlOpacity }}
          >
            {labels}
          </motion.div>,
          target
        )}
        <ProjectedAxisLabels
          axis="x"
          labels={labelsToShow.map((item, index) => ({
            key: item.key,
            text: displayLabels[index] ?? item.label,
            x: item.x - margin.left,
            y: projection.frame.depth,
            z: 0,
          }))}
        />
      </>
    );
  }

  if (framed) {
    return createPortal(
      // min-h-8 reserves the date pill's own height, which is portalled into
      // this rail on hover and would otherwise be clipped by the scroller.
      <div className={cn("pointer-events-none", CHART_X_AXIS_ROW_CLASS)}>
        {labels}
      </div>,
      target
    );
  }

  return createPortal(
    <div className="pointer-events-none absolute inset-0">{labels}</div>,
    target
  );
});

XAxis.displayName = "XAxis";

export default XAxis;
